import * as THREE from 'three';

/** A small black cat that bolts down the dark shrine path: the game's one honest hint. */
export class Cat {
  readonly group = new THREE.Group();
  private readonly body: THREE.Group;
  private readonly tail: THREE.Mesh;
  private readonly materials: THREE.Material[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private waypoints: THREE.Vector3[] = [];
  private index = 0;
  private home = new THREE.Vector3();
  state: 'sitting' | 'running' | 'gone' = 'sitting';
  private time = 0;

  constructor() {
    const fur = new THREE.MeshStandardMaterial({ color: '#0b0b0c', roughness: 0.8 });
    const eye = new THREE.MeshBasicMaterial({ color: '#d8ff6a' });
    eye.color.multiplyScalar(2.5);
    this.materials.push(fur, eye);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const sphere = new THREE.SphereGeometry(0.5, 10, 8);
    this.geometries.push(box, sphere);
    this.body = new THREE.Group();
    this.group.add(this.body);
    const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, sx: number, sy: number, sz: number) => {
      const m = new THREE.Mesh(geo, mat);
      m.position.set(x, y, z);
      m.scale.set(sx, sy, sz);
      m.castShadow = true;
      this.body.add(m);
      return m;
    };
    add(sphere, fur, 0, 0.2, 0, 0.2, 0.2, 0.42);
    add(sphere, fur, 0, 0.3, -0.22, 0.17, 0.16, 0.16);
    add(box, fur, -0.05, 0.4, -0.24, 0.04, 0.07, 0.03);
    add(box, fur, 0.05, 0.4, -0.24, 0.04, 0.07, 0.03);
    add(sphere, eye, -0.04, 0.32, -0.3, 0.025, 0.025, 0.02);
    add(sphere, eye, 0.04, 0.32, -0.3, 0.025, 0.025, 0.02);
    for (const [x, z] of [[-0.07, -0.12], [0.07, -0.12], [-0.07, 0.13], [0.07, 0.13]]) add(box, fur, x, 0.07, z, 0.05, 0.14, 0.05);
    this.tail = add(box, fur, 0, 0.3, 0.26, 0.035, 0.3, 0.035);
    this.tail.rotation.x = 0.5;
  }

  configure(home: THREE.Vector3, waypoints: THREE.Vector3[]): void {
    this.home.copy(home);
    this.waypoints = waypoints;
    this.reset();
  }

  reset(): void {
    this.group.position.copy(this.home);
    this.group.rotation.y = Math.PI / 2;
    this.group.visible = true;
    this.state = 'sitting';
    this.index = 0;
  }

  /** Returns true on the frame the cat starts running. */
  update(dt: number, playerPos: THREE.Vector3): boolean {
    this.time += dt;
    let started = false;
    if (this.state === 'sitting') {
      this.tail.rotation.z = Math.sin(this.time * 2) * 0.3;
      if (playerPos.distanceTo(this.group.position) < 9) {
        this.state = 'running';
        started = true;
      }
    } else if (this.state === 'running') {
      const target = this.waypoints[this.index];
      if (!target) {
        this.state = 'gone';
        this.group.visible = false;
        return started;
      }
      const p = this.group.position;
      const dx = target.x - p.x;
      const dz = target.z - p.z;
      const d = Math.hypot(dx, dz);
      const step = 6.5 * dt;
      if (d <= step) {
        p.copy(target);
        this.index += 1;
      } else {
        p.x += (dx / d) * step;
        p.z += (dz / d) * step;
        this.group.rotation.y = Math.atan2(-dx, -dz);
      }
      this.body.position.y = Math.abs(Math.sin(this.time * 18)) * 0.06;
      this.tail.rotation.x = 1.1;
    }
    return started;
  }

  dispose(): void {
    for (const m of this.materials) m.dispose();
    for (const g of this.geometries) g.dispose();
  }
}
